import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import hotelLoaderGif from "../../../../assets/media/hotel-loader.gif";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";

export const HotelSearchLoader = ({
  city = "",
  inBig = "",
  inDow = "",
  outBig = "",
  outDow = "",
  nights = 0,
  rooms = 0,
  adults = 0,
  children = 0,
  mode = "search",
}) => {
  const [pct, setPct] = useState(0);

  useBodyScrollLock();

  useEffect(() => {
    const t = setInterval(() => {
      setPct((p) => {
        if (p >= 90) return 90;
        const np = p + 0.8 * (p > 80 ? 0.3 : 1);
        return np >= 90 ? 90 : np;
      });
    }, 60);
    return () => clearInterval(t);
  }, []);

  const p = Math.round(pct);
  const stage = pct < 35 ? 0 : pct < 75 ? 1 : 2;

  const stepLabels = useMemo(
    () => mode === "checkout" 
      ? ["Connecting to the property", "Verifying room availability", "Preparing checkout"]
      : [`Searching hotels${city ? ` in ${city}` : ""}`, "Comparing room rates", "Applying best deals"],
    [city, mode]
  );
  const guestsLabel = useMemo(() => {
    const parts = [];
    if (adults) parts.push(`${adults} ${adults > 1 ? "Adults" : "Adult"}`);
    if (children) parts.push(`${children} ${children > 1 ? "Children" : "Child"}`);
    return parts.join(", ");
  }, [adults, children]);

  return createPortal(
    <div className="hsl-overlay">
      <style>{`
        .hsl-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:24px;z-index:99999}
        .hsl-modal{width:100%;max-width:500px;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 24px 60px rgba(15,25,55,.24);animation:hsl-pop .4s cubic-bezier(.2,.8,.2,1) both}
        .hsl-head{background:#F3F7FF;border-bottom:1px solid #E3EAFB;padding:28px 30px 24px;display:flex;flex-direction:column;gap:20px}
        .hsl-route{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:20px}
        .hsl-lbl{font-size:12px;font-weight:600;color:#000}
        .hsl-date{font-size:26px;font-weight:800;line-height:1;letter-spacing:-.01em;color:#1B1F2B}
        .hsl-dow{font-size:14px;color:#000}
        .hsl-end.right{text-align:right}
        .hsl-hotel{display:flex;align-items:center;justify-content:center}
        .hsl-gif{width:72px;height:72px;object-fit:contain}
        .hsl-chips{display:flex;flex-wrap:wrap;gap:6px;font-size:13px;font-weight:600;color:#1B1F2B}
        .hsl-chip{background:#fff;border:1px solid #E3EAFB;padding:6px 12px;border-radius:999px}
        .hsl-body{padding:24px 30px 28px;display:flex;flex-direction:column;gap:20px}
        .hsl-title{font-size:18px;font-weight:700;color:#1B1F2B}
        .hsl-steps{display:flex;flex-direction:column;gap:12px}
        .hsl-step{display:flex;align-items:center;gap:12px;font-size:14px;font-weight:600}
        .hsl-ic{width:20px;height:20px;border-radius:50%;box-sizing:border-box;flex:none;display:flex;align-items:center;justify-content:center}
        .hsl-ic.done{background:#2F6BF5}
        .hsl-ic.spin{border:2px solid #D5E0FB;border-top-color:#2F6BF5;animation:hsl-spin .8s linear infinite}
        .hsl-ic.pend{border:2px solid #E4E6EB}
        .hsl-track{height:6px;border-radius:999px;background:#EEF1F6;overflow:hidden}
        .hsl-fill{height:100%;background:#2F6BF5;border-radius:999px;transition:width .2s linear}
        .hsl-progrow{display:flex;justify-content:space-between;font-size:12px;font-weight:600;color:#5B6070;margin-top:8px}
        @keyframes hsl-pop{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
        @keyframes hsl-spin{to{transform:rotate(360deg)}}
      `}</style>

      <div className="hsl-modal">
        <div className="hsl-head">
          <div className="hsl-route">
            <div className="hsl-end">
              <div className="hsl-lbl">Check-in</div>
              <div className="hsl-date">{inBig || "—"}</div>
              {inDow ? <div className="hsl-dow">{inDow}</div> : null}
            </div>
            <div className="hsl-hotel">
              <img src={hotelLoaderGif} alt="" className="hsl-gif" />
            </div>
            <div className="hsl-end right">
              <div className="hsl-lbl">Check-out</div>
              <div className="hsl-date">{outBig || "—"}</div>
              {outDow ? <div className="hsl-dow">{outDow}</div> : null}
            </div>
          </div>
          <div className="hsl-chips">
            {city ? <span className="hsl-chip">{city}</span> : null}
            {nights ? <span className="hsl-chip">{nights} {nights > 1 ? "Nights" : "Night"}</span> : null}
            {rooms ? <span className="hsl-chip">{rooms} {rooms > 1 ? "Rooms" : "Room"}</span> : null}
            {guestsLabel ? <span className="hsl-chip">{guestsLabel}</span> : null}
          </div>
        </div>

        <div className="hsl-body">
          <div className="hsl-title">
            {mode === "checkout" 
              ? "Hold on, we're confirming your room selection" 
              : "Hold on, we're finding the best stays for you"}
          </div>

          <div className="hsl-steps">
            {stepLabels.map((label, i) => (
              <div className="hsl-step" key={i}>
                {i < stage ? (
                  <span className="hsl-ic done"><svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2"><path d="M2 6.5 5 9l5-6" /></svg></span>
                ) : i === stage ? (
                  <span className="hsl-ic spin" />
                ) : (
                  <span className="hsl-ic pend" />
                )}
                <span style={{ color: i > stage ? "#9AA0AD" : "#1B1F2B" }}>{label}</span>
              </div>
            ))}
          </div>

          <div>
            <div className="hsl-track"><div className="hsl-fill" style={{ width: p + "%" }} /></div>
            <div className="hsl-progrow">
              <span>{mode === "checkout" ? "Confirming details..." : "Comparing 1,200+ properties"}</span>
              <span>{p}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HotelSearchLoader;
